// check if search is valid email, phone, dob

import {
  AssessmentInterface,
  BaseQuery,
  ClaimInterface,
  PracticeAssessmentData,
  removeArrayContainsFilter,
} from '@hb/shared'
import { toPatientSearchQuery } from './patients'

type AssessmentSearchInterface = AssessmentInterface | ClaimInterface

const toAuthReferenceNumberQuery = <
  T extends AssessmentSearchInterface | PracticeAssessmentData =
    | AssessmentSearchInterface
    | PracticeAssessmentData,
>(
  baseQuery: BaseQuery<T>,
  referenceNumber: string,
): BaseQuery<T> => ({
  ...baseQuery,
  filters: [
    ...removeArrayContainsFilter(baseQuery.filters),
    ['authRefNumbers', 'array-contains', referenceNumber],
  ],
})

const toClaimReferenceNumberQuery = <
  T extends AssessmentSearchInterface | PracticeAssessmentData =
    | AssessmentSearchInterface
    | PracticeAssessmentData,
>(
  baseQuery: BaseQuery<T>,
  referenceNumber: string,
): BaseQuery<T> => ({
  ...baseQuery,
  filters: [
    ...removeArrayContainsFilter(baseQuery.filters),
    ['claimRefNumbers', 'array-contains', referenceNumber],
  ],
})

const toEftCheckRefNumberQuery = <
  T extends AssessmentSearchInterface | PracticeAssessmentData =
    | AssessmentSearchInterface
    | PracticeAssessmentData,
>(
  baseQuery: BaseQuery<T>,
  referenceNumber: string,
): BaseQuery<T> => ({
  ...baseQuery,
  filters: [
    ...removeArrayContainsFilter(baseQuery.filters),
    ['eftCheckRefNumbers', 'array-contains', referenceNumber],
  ],
})

const toColonCommandQuery = <
  T extends AssessmentSearchInterface | PracticeAssessmentData =
    | AssessmentSearchInterface
    | PracticeAssessmentData,
>(
  baseQuery: BaseQuery<T>,
  colonCommand: string,
  colonParam: string,
): BaseQuery<T> | null => {
  if (!colonParam) {
    throw new Error('Colon parameter is required for command: ' + colonCommand)
  }
  const command = colonCommand.toLowerCase().trim()
  const param = colonParam.toLowerCase().trim()
  switch (command) {
    case 'auth':
      return toAuthReferenceNumberQuery(baseQuery, param)
    case 'claim':
      return toClaimReferenceNumberQuery(baseQuery, param)
    case '$':
      return toEftCheckRefNumberQuery(baseQuery, param)
    default:
      break
  }
  return null
}

export const toPracticeColonCommandQuery = <
  T extends PracticeAssessmentData = PracticeAssessmentData,
>(
  baseQuery: BaseQuery<T>,
  colonCommand: string,
  colonParam: string,
): BaseQuery<T> | null => {
  if (!colonParam) {
    throw new Error('Colon parameter is required for command: ' + colonCommand)
  }
  const command = colonCommand.toLowerCase().trim()
  const param = colonParam.toLowerCase().trim()
  switch (command) {
    case '$':
      return toEftCheckRefNumberQuery<T>(baseQuery, param)
    default:
      break
  }
  return null
}

export const toPracticeAssessmentSearchQuery = <
  T extends PracticeAssessmentData = PracticeAssessmentData,
>(
  baseQuery: BaseQuery<T>,
  search: string,
): BaseQuery<T> | null => {
  const [colonCommand, colonParam] = search.split(':')
  if (colonCommand && colonParam)
    return toPracticeColonCommandQuery(baseQuery, colonCommand, colonParam)

  return toPatientSearchQuery(baseQuery, search)
}

// if none of these, search by name
export const toAdminAssessmentSearchQuery = <
  T extends AssessmentSearchInterface = AssessmentSearchInterface,
>(
  baseQuery: BaseQuery<T>,
  search: string,
): BaseQuery<T> | null => {
  // handle colon commands

  const [colonCommand, colonParam] = search.split(':')
  if (colonCommand && colonParam) return toColonCommandQuery(baseQuery, colonCommand, colonParam)

  return toPatientSearchQuery(baseQuery, search)
}
